import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Tabs from 'components/partials/tabs/tabs';
import toast from 'components/partials/toast/toast';
import CriticalElementHeader from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-details-header/critical-element-details-header';
import CriticalElementForm from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-form/critical-element-form';
import RelatedGoals from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/related-goals/related-goals';
import History from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-tabs/critical-element-history-tab';
import Monitoring from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-tabs/critical-element-monitoring-tab';
import {
  CriticalElementFormProvider,
  useCriticalElementForm,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/context/context';

import type { CriticalElementDto } from 'api/generated/schemas';

import {
  createClientCriticalElement,
  deleteClientCriticalElement,
  updateClientCriticalElement,
} from 'api/generated/critical-elements/critical-elements';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import {
  getPathToElements,
  prepareElementPayload as preparePayload,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-elements.helpers';
import { renderModal } from 'helpers/modal-render';
import {
  getTabItems,
  getEmptyElement,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element.helpers';

import Icons from 'assets/icons';

import type { CriticalElement as CriticalElementType } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

interface CriticalElementProps {
  data: CriticalElementType | null;
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  loading: boolean;
  getElement: () => Promise<void>;
  onSaveSuccess?: () => void;
  onCancel?: () => void;
  setLoading: (value: boolean) => void;
}

const CriticalElement = ({
  data,
  isEditMode,
  setIsEditMode,
  loading,
  getElement,
  onSaveSuccess,
  onCancel,
  setLoading,
}: CriticalElementProps): React.JSX.Element => {
  const params = useParams<{ id: string; year: string }>();
  const { id: elementId, year } = params;

  const selectedElementId = elementId;
  const isElementCreation = elementId === 'new';

  const defaultValues = isElementCreation
    ? getEmptyElement(Number(year))
    : data;

  return (
    <CriticalElementFormProvider
      defaultValues={(defaultValues || {}) as never}
      loading={loading}
    >
      <CriticalElementInner
        data={data}
        isEditMode={isEditMode}
        isCreation={isElementCreation}
        setIsEditMode={setIsEditMode}
        selectedElementId={selectedElementId}
        getElement={getElement}
        onSaveSuccess={onSaveSuccess}
        onCancel={onCancel}
        year={year}
        setLoading={setLoading}
      />
    </CriticalElementFormProvider>
  );
};

interface CriticalElementInnerProps {
  data: CriticalElementType | null;
  isEditMode: boolean;
  isCreation: boolean;
  setIsEditMode: (value: boolean) => void;
  selectedElementId: string | undefined;
  getElement: () => Promise<void>;
  onSaveSuccess?: () => void;
  onCancel?: () => void;
  year: string | undefined;
  setLoading: (value: boolean) => void;
}

const CriticalElementInner = ({
  data,
  isEditMode,
  isCreation,
  setIsEditMode,
  selectedElementId,
  getElement,
  onSaveSuccess,
  onCancel,
  year,
  setLoading,
}: CriticalElementInnerProps): React.JSX.Element => {
  const [element, setElement] = useState<CriticalElementType | null>(data);
  const [activeTab, setActiveTab] = useState<string>('details');
  const { getValues, handleSubmit, reset } = useCriticalElementForm();
  const relatedGoalsSaveRef = useRef<
    ((newId?: string | number) => Promise<void>) | undefined
  >(undefined);

  const params = useParams<{ clientId?: string; id?: string }>();
  const navigate = useNavigate();
  const clientId = params.clientId || params.id;

  const user = useCurrentUser() as CurrentUserShape;
  const userId = user?.data?.oktaUserId;

  useEffect(() => {
    if (isCreation && !isEditMode) {
      setIsEditMode(true);
    }
  }, [isCreation, isEditMode, setIsEditMode]);

  useEffect(() => {
    if (isCreation) {
      const newElement = getEmptyElement(Number(year));

      setElement(newElement as unknown as CriticalElementType);
      reset(newElement as never);
    } else {
      setElement(data);
    }
    setActiveTab('details');
  }, [data, isCreation, reset, selectedElementId, year]);

  const handleEdit = useCallback((): void => {
    setIsEditMode(true);
  }, [setIsEditMode]);

  const handleCancel = useCallback((): void => {
    if (isCreation) {
      if (onCancel) {
        onCancel();

        return;
      }

      const pathToElements = getPathToElements(window.location.pathname);

      navigate(pathToElements);

      return;
    }

    setIsEditMode(false);
    if (data) {
      reset(data as never);
    }
  }, [setIsEditMode, reset, data, isCreation, navigate, onCancel]);

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!userId || clientId == null || clientId === '') {
      return;
    }

    setLoading(true);
    const formData = getValues();

    formData.enteringClassYear = year;

    const payload = preparePayload({
      userId,
      clientId,
      data: element,
      formData: formData as never,
    });

    try {
      let savedElementId = element?.id;

      if (element?.id) {
        await updateClientCriticalElement(
          userId,
          Number(clientId),
          Number(element.id),
          // TODO(ts-migration): preparePayload vs CriticalElementDto — migration-debt.md (2026-05-07 CE migration)
          payload.elementData as unknown as CriticalElementDto,
        );

        toast.success({
          title: 'The critical element was successfully updated',
        });

        if (relatedGoalsSaveRef.current) {
          await relatedGoalsSaveRef.current();
        }

        await getElement();
        setIsEditMode(false);
      } else {
        const created = await createClientCriticalElement(
          userId,
          Number(clientId),
          // TODO(ts-migration): preparePayload vs CriticalElementDto — migration-debt.md (2026-05-07 CE migration)
          payload.elementData as unknown as CriticalElementDto,
        );

        savedElementId = created?.id;

        toast.success({
          title: 'The critical element was successfully created',
        });

        if (relatedGoalsSaveRef.current && savedElementId) {
          await relatedGoalsSaveRef.current(savedElementId);
        }

        if (onSaveSuccess) {
          onSaveSuccess();

          return;
        }

        const pathToElements = getPathToElements(window.location.pathname);

        navigate(pathToElements);
      }
    } catch (error) {
      console.error('Error saving critical element:', error);
      toast.error({
        title: 'Something went wrong',
      });
    }
  }, [
    getValues,
    element,
    userId,
    clientId,
    setIsEditMode,
    getElement,
    navigate,
    onSaveSuccess,
    relatedGoalsSaveRef,
    year,
  ]);

  const handleFormSubmit = handleSubmit(onSubmit);

  const handleDelete = useCallback(async (): Promise<void> => {
    if (!element?.id || !userId || clientId == null || clientId === '') return;

    try {
      await deleteClientCriticalElement(
        userId,
        Number(clientId),
        Number(element.id),
      );

      toast.success({
        title: 'The critical element was successfully removed',
      });

      if (onSaveSuccess) {
        onSaveSuccess();

        return;
      }

      const pathToElements = getPathToElements(window.location.pathname);

      navigate(pathToElements);
    } catch (error) {
      console.error('Error deleting critical element:', error);
      toast.error({
        title: 'Something went wrong',
      });
    }
  }, [element?.id, userId, clientId, navigate, onSaveSuccess]);

  const getActionMenuItems = (): Array<{
    label: string;
    icon: React.JSX.Element;
    isDelete: boolean;
    onClick: () => void;
  }> => [
    {
      label: 'Delete critical element',
      icon: <Icons.Delete customClass="text-critical-500 mr-2" />,
      isDelete: true,
      onClick: () =>
        renderModal({
          headerText: `Are you sure you want to delete the critical element?`,
          onConfirmCallback: () => {
            handleDelete();
          },
          onConfirmButtonLabel: 'Delete critical element',
        } as never),
    },
  ];

  const renderFormContent = (): React.JSX.Element => (
    <div className="flex gap-x-9">
      <div className="w-1/2">
        <CriticalElementForm
          data={element}
          isEditing={isEditMode}
          isCreation={isCreation}
          userId={userId}
        />
      </div>
      <div className="w-1/2">
        <RelatedGoals
          clientId={clientId}
          criticalElementId={element?.id}
          enteringClassYear={element?.enteringClassYear as string | number | undefined}
          isEditMode={isEditMode}
          onSave={relatedGoalsSaveRef}
        />
      </div>
    </div>
  );

  const renderHistory = (): React.JSX.Element => (
    <History data={element} userId={userId} clientId={clientId} />
  );

  const renderTabContent = (): React.JSX.Element | null => {
    switch (activeTab) {
      case 'details':
        return renderFormContent();
      case 'monitoring':
        return (
          <Monitoring
            criticalElementId={element?.id}
            clientId={clientId}
            userId={userId}
            entryTerm={element?.enteringClassYear as string | number | undefined}
          />
        );
      case 'history':
        return renderHistory();
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-full pt-4">
      <CriticalElementHeader
        isEditMode={isEditMode}
        onEditClick={handleEdit}
        customClass="mb-5"
        isNew={isCreation}
        onCancel={handleCancel}
        onSubmit={handleFormSubmit}
        activeTab={activeTab}
        actionMenuItems={getActionMenuItems()}
        title={data?.summary}
      />
      <Tabs
        items={getTabItems(!element?.id || isEditMode)}
        activeTab={activeTab}
        onSelect={setActiveTab}
        customClass="mb-3"
        data-testid="critical-element-tabs"
      />

      {renderTabContent()}
    </div>
  );
};

export default CriticalElement;
